import React, { useEffect, useState } from "react"
import PageTitleButton from "../../components/PageTitleButton"
import { Button, Card, Table } from "react-bootstrap"
import TooltipComponent from "../../components/Tooltip"
import StationReviewSkeleton from "../../skeleton/stationReviews"
import { toast } from "react-toastify"
import axiosProvider from "../../lib/AxiosProvider"
import { admin } from "../../config/apiEndpoints"
import {
  getStatusClassName,
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../lib/GlobalMethods"
import useDebounce from "../../lib/hooks/useDebounce"
import InfiniteScroll from "react-infinite-scroll-component"
import recordNotFound from "../../assets/images/record_not_found.png"
import SweetAlerts from "../../components/SweetAlerts"
import moment from "moment"
import { modulePermission } from "../../lib/GlobalVariables"

const StationReviews = () => {
  const canEdit = HasModulePermission(modulePermission.station, "edit")

  const [data, setData] = useState(null)
  const [show, setShow] = useState(false)
  const [isView, setIsView] = useState(false)
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [approve, setApprove] = useState(true)
  const [showAlert, setShowAlert] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleShow = () => setShow(true)
  const handleClose = () => {
    setShow(false)
    setShowAlert(false)
    setIsUpdate(false)
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.reviewList}?page=${
          pagination.current_page
        }&search_term=${searchQuery ? searchQuery : ""}&limit=15`,
      })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      handleAccess({ code: response?.data?.code })

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const handleSubmit = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateReview + id,
        data: { status: approve ? "Approved" : "Rejected" },
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) =>
          data.map((review) =>
            review.id === id
              ? { ...review, status: response?.data?.data?.status }
              : review
          )
        )
        setLoading(false)
      } else {
        setLoading(false)
        throw { message: response?.data?.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, pagination.current_page])

  return (
    <>
      <PageTitleButton PagetitleText={"Station Reviews"} />

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<StationReviewSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th className="text-center">Station ID</th>
                    <th className="text-center">Booking ID</th>
                    <th className="text-center">User Name</th>
                    <th className="text-center">Station Name</th>
                    <th className="text-center">Rating</th>
                    <th>Comments</th>
                    <th className="text-center">Review Date / Time</th>
                    <th className="text-center">Status</th>
                    {canEdit && <th className="text-center">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data?.map((item, index) => (
                      <tr key={index + 1}>
                        <td className="text-center">{item?.station?.id}</td>
                        <td className="text-center">{item?.booking?.id}</td>
                        <td className="text-center">{item?.user?.name}</td>
                        <td className="text-center fs_14">
                          {item?.station?.station_name}
                        </td>
                        <td className="text-center">
                          <div className="star_reviews_wrapper">
                            {Array.from({ length: 5 }, (_, i) => (
                              <i
                                key={i}
                                className={`icon_main ${
                                  i < item.rating
                                    ? "fillstar_review"
                                    : "star_review"
                                }`}
                              ></i>
                            ))}
                          </div>
                        </td>
                        <td className="w-50 fs_14">{item.comment}</td>
                        <td className="text-center fs_14">
                          <p className="mb-0 text_7gray lh-1">
                            {moment(item?.createdAt).format("YYYY-MM-DD")}
                          </p>
                          <span className="mb-0 text-uppercase fw-semibold">
                            {moment(item?.createdAt).format("hh:mm A")}
                          </span>
                        </td>
                        <td className="text-center">
                          <span
                            className={`badges ${getStatusClassName(
                              item.status
                            )} fs_14 fw-normal px-3 py-1`}
                          >
                            {item?.status}
                          </span>
                        </td>
                        {canEdit && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {item?.status !== "Approved" && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Approve"
                                >
                                  <Button
                                    variant="btnedit_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setApprove(true)
                                      setShowAlert(true)
                                      setIsUpdate(item)
                                    }}
                                  >
                                    <i className="icon_main checked_circle"></i>
                                  </Button>
                                </TooltipComponent>
                              )}

                              {item?.status !== "Rejected" && (
                                <TooltipComponent
                                  toolplace="top"
                                  tooltipText="Disapprove"
                                >
                                  <Button
                                    variant="btndelete_icon"
                                    className="btn_icon"
                                    onClick={() => {
                                      setApprove(false)
                                      setShowAlert(true)
                                      setIsUpdate(item)
                                    }}
                                  >
                                    <i className="icon_main cross_circle"></i>
                                  </Button>
                                </TooltipComponent>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data && !data?.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={9}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <StationReviewSkeleton />
      )}

      <SweetAlerts
        showAlert={showAlert}
        loading={loading}
        alertMessage={`Are you sure you want to ${
          approve ? "Approve" : "Reject"
        } this Comment?`}
        handleClose={handleClose}
        iconClassName={approve ? "exclamation_circle" : "exclamation_circle"}
        handleAlert={() => {
          handleSubmit(isUpdate.id)
        }}
      />
    </>
  )
}

export default StationReviews
